import i18n from '@/lib/i18n';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { getMyRestaurant, updateMyRestaurant, type RestaurantUpdate } from '@/lib/api/restaurants';
import type { Restaurant, DayOfWeek, SubscriptionPlan } from '@/types';

/**
 * The signed-in user's restaurant, as the API reports it.
 *
 * This store used to be seeded from a `mockRestaurant` literal — "Bella Vista
 * Cafe", a stock Unsplash logo, 08:00-22:00 hours and a 'professional' plan
 * that the backend has never issued. `loadRestaurant` slept 500ms and assigned
 * it. Every restaurant, on every screen, rendered that one fictional cafe's
 * identity while the owner's own name (which does come from the API) rendered
 * correctly beside it. There is no fallback record any more: if the request
 * fails, `restaurant` stays null and callers render an em dash.
 */

interface RestaurantStore {
  // State
  restaurant: Restaurant | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadRestaurant: () => Promise<void>;
  updateRestaurant: (updates: RestaurantUpdate) => Promise<void>;

  // Getters
  getDisplayName: () => string;
  getCurrentPlan: () => SubscriptionPlan | null;
  canAccessFeature: (feature: string) => boolean;
  getBrandingColors: () => { primary: string; secondary: string };

  // Cleanup
  clearRestaurantData: () => void;
}

/** Super Admin operates at platform level and has no restaurant of its own. */
function isSuperAdmin(): boolean {
  const authData = localStorage.getItem('nova-auth');
  if (!authData) return false;

  try {
    return JSON.parse(authData).state?.user?.role === 'super_admin';
  } catch {
    return false;
  }
}

export const useRestaurantStore = create<RestaurantStore>()(
  persist(
    immer((set, get) => ({
      restaurant: null,
      isLoading: false,
      error: null,

      loadRestaurant: async () => {
        if (isSuperAdmin()) {
          set(draft => {
            draft.restaurant = null;
            draft.isLoading = false;
          });
          return;
        }

        set(draft => {
          draft.isLoading = true;
          draft.error = null;
        });

        try {
          const restaurant = await getMyRestaurant();
          set(draft => {
            draft.restaurant = restaurant;
            draft.isLoading = false;
          });
        } catch (error) {
          // Leave `restaurant` null rather than substituting a placeholder —
          // a wrong restaurant name is worse than a missing one.
          set(draft => {
            draft.restaurant = null;
            draft.error = error instanceof Error ? error.message : 'Failed to load restaurant';
            draft.isLoading = false;
          });
        }
      },

      /**
       * Save the owner's edits. The server merges nested objects and answers
       * with the whole record, so the response replaces local state outright —
       * no optimistic patching that could drift from what was actually stored.
       */
      updateRestaurant: async (updates: RestaurantUpdate) => {
        set(draft => {
          draft.isLoading = true;
          draft.error = null;
        });

        try {
          const restaurant = await updateMyRestaurant(updates);
          set(draft => {
            draft.restaurant = restaurant;
            draft.isLoading = false;
          });
        } catch (error: any) {
          const fieldErrors = error?.response?.data?.errors;
          const message = Array.isArray(fieldErrors) && fieldErrors.length
            ? fieldErrors.map((e: any) => e.msg ?? e.message).filter(Boolean).join('; ')
            : error?.response?.data?.message || error?.message || i18n.t('restaurantSettings.saveFailed');

          set(draft => {
            draft.error = message;
            draft.isLoading = false;
          });
          throw new Error(message);
        }
      },

      getDisplayName: () => {
        return get().restaurant?.name || i18n.t('brand.name');
      },

      getCurrentPlan: () => {
        return get().restaurant?.subscription.plan ?? null;
      },

      // Feature flags come from the restaurant's own subscription.features,
      // which the backend derives from the plan.
      canAccessFeature: (feature: string) => {
        return get().restaurant?.subscription.features.includes(feature) ?? false;
      },

      getBrandingColors: () => {
        const branding = get().restaurant?.branding;
        return {
          primary: branding?.primaryColor || '#2563eb',
          secondary: branding?.secondaryColor || '#1e40af',
        };
      },

      clearRestaurantData: () => {
        set(draft => {
          draft.restaurant = null;
          draft.isLoading = false;
          draft.error = null;
        });
        localStorage.removeItem('nova-restaurant');
      },
    })),
    {
      name: 'nova-restaurant',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        // Never restore a restaurant for a signed-out or platform-level user.
        const authData = localStorage.getItem('nova-auth');
        let shouldClearData = true;

        if (authData) {
          try {
            const auth = JSON.parse(authData);
            if (auth.state?.isAuthenticated && auth.state?.user?.role !== 'super_admin') {
              shouldClearData = false;
            }
          } catch {
            console.warn('Error parsing auth data during restaurant rehydration');
          }
        }

        if (shouldClearData && state) {
          state.restaurant = null;
        }
      },
      partialize: (state) => {
        if (!state.restaurant || isSuperAdmin()) return {};
        return { restaurant: state.restaurant };
      },
    }
  )
);

const DAY_KEYS: DayOfWeek[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

export const restaurantHelpers = {
  /**
   * Today's hours as a display string, or null when the restaurant has no
   * hours on record. Callers render an em dash for null rather than guessing.
   */
  formatOperatingHours: (restaurant: Restaurant | null): string | null => {
    if (!restaurant) return null;

    const today = restaurant.todayOperatingHours
      ?? restaurant.settings?.operatingHours?.[DAY_KEYS[new Date().getDay()]];

    if (!today) return null;
    if (!today.isOpen) return i18n.t('restaurant.closedToday');

    return i18n.t('restaurant.openHours', { open: today.open, close: today.close });
  },

  hasActiveSubscription: (restaurant: Restaurant | null): boolean => {
    return restaurant?.subscription.status === 'active';
  },
};

export default useRestaurantStore;
